import { isNotFulfillable } from './promo-fulfillment-utils';
import {
  isBackwardsBogo as isBackwardsBogoFn,
  isForwardsB1gy as isForwardsB1gyFn,
  isDollarThresholdBogo as isDollarThresholdBogoFn,
  isForwardsBogo as isForwardsBogoFn,
  pipe
} from './promo-utils';
import {
  BACKWARDS_BOGO_SUBEXPERIENCE_TAGS,
  BMSM_SUBEXPERIENCE_TAGS,
  DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS,
  EXPERIENCE_TAGS,
  FORWARDS_BOGO_SUBEXPERIENCE_TAGS,
  MA,
  MAX_NUM_TIERS_IN_PROMOTION,
  MSB_SUBEXPERIENCE_TAGS,
  NVALUE_REFINEMENT_SEPARATOR
} from './constants';

const isValidBogoData = (promotion) => {
  const { subExperienceTag, reward } = promotion;

  switch (subExperienceTag) {
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne:
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetY:
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetYPercentageOff:
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetYDollarOff:
  case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne:
  case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetOne:
  case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetPercentageOff:
  case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetDollarOff:
    return true;
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff:
  case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff:
    return Boolean(
      reward.tiers.length === 1
        && reward.tiers[0].minPurchaseQuantity === 1
        && reward.tiers[0].rewardPercent
    );
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff:
  case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff:
    return Boolean(
      reward.tiers.length === 1
        && reward.tiers[0].minPurchaseQuantity === 1
        && reward.tiers[0].rewardAmountPerOrder
    );
  default:
    return false;
  }
};

/**
 * Given an array of reward tiers, this function will return true
 * if all reward tiers contain *at least* the same properties in the first reward tier.
 * @param {Array} rewardTiers
 * @returns {boolean} hasSameTiers
 */
const allTiersHaveSameDetails = (rewardTiers) => {
  const {
    minPurchaseQuantity,
    minPurchaseAmount,
    rewardPercent,
    rewardAmountPerOrder,
    rewardAmountPerItem,
    rewardFixedPrice
  } = rewardTiers[0];

  return rewardTiers.every((tier) => {
    if (rewardPercent && !tier.rewardPercent) return false;
    if (rewardAmountPerItem && !tier.rewardAmountPerItem) return false;
    if (rewardAmountPerOrder && !tier.rewardAmountPerOrder) return false;
    if (minPurchaseAmount && !tier.minPurchaseAmount) return false;
    if (minPurchaseQuantity && !tier.minPurchaseQuantity) return false;
    if (rewardFixedPrice && !tier.rewardFixedPrice) return false;
    return true;
  });
};

const getMinimumPrice = (rewardTiers) => {
  return rewardTiers.reduce((currentMinPrice, { rewardFixedPrice }) => Math.max(currentMinPrice, rewardFixedPrice), 0);
};

const isValidTierData = (pricing) => {
  const rewardTiers = pricing.conditionalPromotions[0].reward.tiers;
  // make sure the item is not part of single-sku bulk
  return !pricing.alternate?.bulk?.value
    && (Array.isArray(rewardTiers) && rewardTiers[0] && typeof rewardTiers[0] === 'object'
      && (rewardTiers[0].minPurchaseAmount || rewardTiers[0].minPurchaseQuantity)
      && (rewardTiers[0].rewardPercent
        || rewardTiers[0].rewardAmountPerOrder
        || rewardTiers[0].rewardAmountPerItem
        || rewardTiers[0].rewardFixedPrice)
      && allTiersHaveSameDetails(rewardTiers));
};

const isValidMSBData = (pricing) => {
  const rewardTiers = pricing.conditionalPromotions[0].reward.tiers;
  if (rewardTiers.length === 1) {
    return isValidTierData(pricing);
  }
  return false;
};

const isValidBMSMData = (pricing) => {
  const rewardTiers = pricing.conditionalPromotions[0].reward.tiers;
  if (
    rewardTiers.length >= 2
    && rewardTiers.length <= MAX_NUM_TIERS_IN_PROMOTION
    && !rewardTiers[0].rewardAmountPerItem
  ) {

    if (!isValidTierData(pricing)) {
      return false;
    }

    // hide component if anchor price is less than minimum for rewardFixedPrice scenarios
    return !(rewardTiers[0].rewardFixedPrice && !(pricing.value >= getMinimumPrice(rewardTiers)));
  }

  return false;
};

const isCompatiblePromotion = (anchorItem) => {
  const pricing = anchorItem?.pricing;
  const conditionalPromotion = pricing?.conditionalPromotions?.[0];
  const eligibilityCriteria = conditionalPromotion?.eligibilityCriteria;
  const hasOmsSkus = eligibilityCriteria?.every((eligibilityCriterion) => {
    return !!eligibilityCriterion.omsSkus?.length;
  });
  const hasTiers = conditionalPromotion?.reward?.tiers?.length;
  const hasItems = (conditionalPromotion?.nvalues?.length || hasOmsSkus);
  const hasPreferredPricing = pricing?.preferredPriceFlag;

  if (hasPreferredPricing || !hasItems || !hasTiers) {
    return false;
  }

  switch (conditionalPromotion.experienceTag) {
  case EXPERIENCE_TAGS.BOGO:
    return isValidBogoData(conditionalPromotion);
  case EXPERIENCE_TAGS.MSB:
    return isValidMSBData(pricing);
  case EXPERIENCE_TAGS.BMSM:
    return isValidBMSMData(pricing);
  default:
    return false;
  }
};

const groupItemsByBrandAndSortAlphabetically = ((products) => (
  products.slice().sort((product1, product2) => {
    const { isSoldOut: isSoldOut1, identifiers: identifiers1 } = product1;
    const { isSoldOut: isSoldOut2, identifiers: identifiers2 } = product2;

    const brand1 = identifiers1?.brandName;
    const brand2 = identifiers2?.brandName;
    const label1 = identifiers1?.productLabel;
    const label2 = identifiers2?.productLabel;

    if (isSoldOut1 < isSoldOut2) return -1;
    if (isSoldOut1 > isSoldOut2) return 1;

    if (!brand1 || !label1) return 1;
    if (!brand2 || !label2) return -1;

    if (brand1 < brand2) return -1;
    if (brand1 > brand2) return 1;

    if (label1 < label2) return -1;
    if (label1 > label2) return 1;

    return 0;
  })
));

/**
 * Keep items that have identifiers, pricing, a price greater than or equal to the minimum,
 * and a valid itemId
 * @returns {Array} filteredItems
 */
const filterItems = ({ items, minimumPrice }) => items.filter((item) => (
  item?.identifiers
  && item.pricing
  && !item.pricing.preferredPriceFlag
  && item.pricing.value >= minimumPrice
));

const isProductSoldOut = (product) => {
  return product.identifiers?.productType === MA ? false : isNotFulfillable(product);
};

const addSoldOut = (product) => {
  return {
    ...product,
    isSoldOut: isProductSoldOut(product),
  };
};

const addSoldOutAndListId = ({ filteredItems, listId }) => (
  filteredItems.map((filteredItem) => ({
    ...filteredItem,
    listId,
    isSoldOut: isProductSoldOut(filteredItem),
  }))
);

const sortAnchorItemToTopOfList = ((items, anchorId) => items.slice().sort((item1, item2) => {
  if (item1.itemId === anchorId) return -1;
  if (item2.itemId === anchorId) return 1;
  return 0;
}));

const collectItemsMap = (items) => {
  const listItemsMap = new Map();
  let listHasFulfillment = false;

  items.forEach((item) => {
    listHasFulfillment = listHasFulfillment || !item.isSoldOut;
    listItemsMap.set(item.itemId, item);
  });

  return {
    listItemIds: [...listItemsMap.keys()],
    listItemsMap,
    listHasFulfillment
  };
};

const getSanitizedRewardTiers = (conditionalPromotion) => {
  const tiers = conditionalPromotion?.reward?.tiers;

  return tiers
    .slice()
    // Sorts promotion tiers from smallest to largest quantity or amount needed to qualify
    .sort((
      { minPurchaseQuantity: aQty, minPurchaseAmount: aAmt },
      { minPurchaseQuantity: bQty, minPurchaseAmount: bAmt }) => {
      return (aQty < bQty || aAmt < bAmt) ? -1 : 1;
    })
    // Guarantees each tier has default value for each key and sets the tier index
    .map((tier, i) => {
      return {
        tier: i,
        minPurchaseAmount: tier.minPurchaseAmount || 0,
        minPurchaseQuantity: tier.minPurchaseQuantity || 0,
        maxAllowedRewardAmount: tier.maxAllowedRewardAmount || 0,
        maxPurchaseQuantity: tier.maxPurchaseQuantity || 0,
        rewardPercent: tier.rewardPercent || 0,
        rewardAmountPerItem: tier.rewardAmountPerItem || 0,
        rewardAmountPerOrder: tier.rewardAmountPerOrder || 0,
        rewardFixedPrice: tier.rewardFixedPrice || 0,
        fixedPriceTotal: Math.ceil(tier.rewardFixedPrice * tier.minPurchaseQuantity) || 0,
      };
    });
};

const getSanitizedItems = ({ items = [], anchorItemId, minimumPrice = 0, listId = 'first' }) => {
  return pipe(
    filterItems,
    (filteredItems) => addSoldOutAndListId({ filteredItems, listId }),
    groupItemsByBrandAndSortAlphabetically,
    (groupedItems) => sortAnchorItemToTopOfList(groupedItems, anchorItemId),
    collectItemsMap
  )({ items, minimumPrice });
};

/**
 * Given a supported promotion, this function will return the sub-experience associated with it.
 * Will return an empty string if no sub-experience can be found.
 * @param {Object} promoData
 * @returns {string} sub-experience
 */
const getSubExperience = (promoData) => {
  if (promoData.experienceTag === EXPERIENCE_TAGS.BOGO) return promoData.subExperienceTag;

  const {
    minPurchaseQuantity,
    minPurchaseAmount,
    rewardPercent,
    rewardAmountPerItem,
    rewardAmountPerOrder,
    rewardFixedPrice
  } = promoData.reward.tiers[0];

  // We skip 3 and 6 so combo of min quantity/amount and reward matches MSB
  if (promoData.experienceTag === EXPERIENCE_TAGS.BMSM) {
    // BuyXorMoreGetYPercent
    if (minPurchaseQuantity && rewardPercent) return BMSM_SUBEXPERIENCE_TAGS.BMSM1;
    // BuyXorMoreSaveYDollar
    if (minPurchaseQuantity && rewardAmountPerOrder) return BMSM_SUBEXPERIENCE_TAGS.BMSM2;
    // BuyXDollarGetYPercent
    if (minPurchaseAmount && rewardPercent) return BMSM_SUBEXPERIENCE_TAGS.BMSM4;
    // BuyXDollarGetYDollar
    if (minPurchaseAmount && rewardAmountPerOrder) return BMSM_SUBEXPERIENCE_TAGS.BMSM5;
    // BuyXPayYDollars
    if (minPurchaseQuantity && rewardFixedPrice) return BMSM_SUBEXPERIENCE_TAGS.BMSM7;
  }

  // MSBONLINE
  if (promoData.experienceTag === EXPERIENCE_TAGS.MSB) {
    // BuyXorMoreGetYPercent
    if (minPurchaseQuantity && rewardPercent) return MSB_SUBEXPERIENCE_TAGS.MSB1;
    // BuyXorMoreSaveYDollar
    if (minPurchaseQuantity && rewardAmountPerOrder) return MSB_SUBEXPERIENCE_TAGS.MSB2;
    // BuyXorMoreSaveYDollarEach
    if (minPurchaseQuantity && rewardAmountPerItem) return MSB_SUBEXPERIENCE_TAGS.MSB3;
    // BuyXDollarGetYPercent
    if (minPurchaseAmount && rewardPercent) return MSB_SUBEXPERIENCE_TAGS.MSB4;
    // BuyXDollarGetYDollar
    if (minPurchaseAmount && rewardAmountPerOrder) return MSB_SUBEXPERIENCE_TAGS.MSB5;
    // BuyXDollarGetYDollarEach
    if (minPurchaseAmount && rewardAmountPerItem) return MSB_SUBEXPERIENCE_TAGS.MSB6;
  }

  return '';
};

const getAnalyticsComponentName = ({ isBogo, isMsb, isBmsm }) => {
  return (isBogo && 'bogo')
    || (isMsb && 'multi-sku bulk')
    || (isBmsm && 'buy more save more') || '';
};

const getAnalyticsSharedSection = ({ isBogo, isMsb, isBmsm }) => {
  return (isBogo && 'bogo')
    || (isMsb && 'multi-sku')
    || (isBmsm && 'buy more save more') || '';
};

const buildRefinedNValue = (originalNvalue = null, refinement) => {
  if (originalNvalue && refinement) {
    return `${originalNvalue}${NVALUE_REFINEMENT_SEPARATOR}${refinement}`;
  }

  return originalNvalue;
};

const isDiffLessThanAMonth = (endDate) => {
  const currDate = new Date();
  const diffInMilliseconds = endDate - currDate;
  const isPromoExpired = diffInMilliseconds < 0;
  const diffInDays = (diffInMilliseconds / (24 * 60 * 60 * 1000));
  return (!isPromoExpired && diffInDays <= 30);
};

const getExpDate = (endDate) => {
  const expDate = new Date(endDate);
  const expDateArr = expDate.toString().split(' ');
  return { month: expDateArr[1], day: expDate.getDate() };
};

const transformItemDataToPromoModel = ({
  firstListProducts,
  secondListProducts,
  anchorItem,
  refinedNvalue,
  isApplianceAnchorAvailable
}) => {
  const products = {
    firstList: firstListProducts || [],
    secondList: secondListProducts || [],
  };
  const conditionalPromotion = anchorItem.pricing.conditionalPromotions[0];
  const src1Criteria = conditionalPromotion?.eligibilityCriteria?.find((criterion) => criterion.itemGroup === 'SRC-1');
  const tgt1Criteria = conditionalPromotion?.eligibilityCriteria?.find((criterion) => criterion.itemGroup === 'TGT-1');
  const src1MinPurchaseAmount = src1Criteria?.minPurchaseAmount;
  const tgt1MinPurchaseAmount = tgt1Criteria?.minPurchaseAmount;
  const startDate = conditionalPromotion.dates?.start;
  const endDate = conditionalPromotion.dates?.end;
  // brittle line. shows promo is expired if no end date in data. confirm req with Stacey
  const isExpiring = isDiffLessThanAMonth(new Date(endDate));
  const expDate = getExpDate(endDate);
  const shortDesc = conditionalPromotion.description?.shortDesc || '';
  const longDesc = conditionalPromotion.description?.longDesc || '';
  const rewardTiers = getSanitizedRewardTiers(conditionalPromotion);
  const subExperience = getSubExperience(conditionalPromotion);
  const isBogo = conditionalPromotion.experienceTag === EXPERIENCE_TAGS.BOGO && !!subExperience;
  const isMsb = conditionalPromotion.experienceTag === EXPERIENCE_TAGS.MSB && !!subExperience;
  const isBmsm = conditionalPromotion.experienceTag === EXPERIENCE_TAGS.BMSM && !!subExperience;
  const isForwardsBogo = isForwardsBogoFn({ subExperienceTag: subExperience });
  const isBackwardsBogo = isBackwardsBogoFn({ subExperienceTag: subExperience });
  const isDollarThresholdBogo = isDollarThresholdBogoFn({ subExperienceTag: subExperience });
  const isForwardsB1gy = isForwardsB1gyFn({ subExperienceTag: subExperience });
  const isAppliance = anchorItem.identifiers?.productType === MA;
  const analyticsComponent = getAnalyticsComponentName({ isBogo, isMsb, isBmsm });
  const analyticsSharedSection = getAnalyticsSharedSection({ isBogo, isMsb, isBmsm });

  // Find greatest rewardFixedPrice if BMSM7
  let minimumPrice = 0;
  if (subExperience === BMSM_SUBEXPERIENCE_TAGS.BMSM7) {
    minimumPrice = getMinimumPrice(rewardTiers);
  }

  // Include anchor item for nValue appliance BMSM promotions
  const anchorItemMatch = ({ itemId }) => itemId === anchorItem.itemId;
  const updatedFirstListProducts = (!refinedNvalue || products.firstList.some(anchorItemMatch))
    ? products.firstList
    : [anchorItem, ...products.firstList.slice(0, 5)];

  const {
    listItemIds: firstListItemIds,
    listItemsMap: firstListItemsMap,
    listHasFulfillment
  } = getSanitizedItems({
    items: updatedFirstListProducts,
    anchorItemId: anchorItem.itemId,
    minimumPrice
  });

  const {
    listItemIds: secondListItemIds,
    listItemsMap: secondListItemsMap,
  } = getSanitizedItems({
    items: products.secondList,
    anchorItemId: anchorItem.itemId,
    listId: 'second'
  });

  const listItemsMap = new Map([...firstListItemsMap, ...secondListItemsMap]);

  if (!listItemsMap.has(anchorItem.itemId)) {
    listItemsMap.set(anchorItem.itemId, addSoldOut(anchorItem));
  }

  const isAnchorFulfillable = (isBogo && isAppliance)
    ? isApplianceAnchorAvailable : !listItemsMap.get(anchorItem.itemId).isSoldOut;

  return {
    allListProductsAndAnchor: listItemsMap,
    isForwardsB1gy,
    isBogo,
    isMsb,
    isBmsm,
    isForwardsBogo,
    isBackwardsBogo,
    isDollarThresholdBogo,
    startDate,
    endDate,
    expDate,
    isExpiring,
    shortDesc,
    longDesc,
    analyticsComponent,
    analyticsSharedSection,
    displayableFirstListItemIds: firstListItemIds,
    displayableSecondListItemIds: secondListItemIds,
    listHasFulfillment,
    isAnchorFulfillable,
    subExperience,
    rewardTiers,
    src1MinPurchaseAmount,
    tgt1MinPurchaseAmount
  };
};

export {
  isCompatiblePromotion,
  transformItemDataToPromoModel,
  getSanitizedItems,
  buildRefinedNValue,
  getExpDate
};
